// BDCalendar.jsx — BD Planning Calendar (swimlane Gantt) + industry/government event catalog.
//
// Encodings, all decided in claude/bd-calendar/RUNBOOK.md and not to be casually changed:
//   · Lanes are government offices — the office holding obligation authority is the
//     row grouping, so "who can actually sign this" is structural, not a field.
//   · Diamonds for point events, bars for windows. A one-hour meeting is not a one-day bar.
//   · Fill colour carries FISCAL EXPOSURE, never the appropriation. The appropriation
//     rides as a monospace code on every mark, so identity never depends on hue.
//   · Hatched = unfunded. Dashed outline = our forecast, not the government's date.
//     Squared-off bar end = the run continues past the visible window.
//   · No dependency arrows — they would imply a certainty about government
//     sequencing that does not exist.
// Every exposure state ships with an icon and a text label so colour never carries
// meaning alone.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { crmService } from '../../services/crmService';
import { useIsHQ } from '../../hooks/useIsHQ';

const C = {
  bg: '#0A0F1C', panel: '#111827', border: '#1E2D45', accent: '#22D3EE',
  text: '#F1F5F9', muted: '#94A3B8', dim: '#6B7280', faint: '#4B5563', gold: '#c4a563',
};

const DAY = 86400000;
const iso = (d) => new Date(d).toISOString().slice(0, 10);
const addDays = (d, n) => new Date(new Date(d).getTime() + n * DAY);

const fmtMoney = (v) => {
  if (v == null || v === '' || Number(v) === 0) return '—';
  const n = Number(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtShort = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—');

// Calendar dates (start_date, deadlines) are date-only columns and arrive as
// UTC midnight. Rendering those through the local timezone shows the previous day
// for anyone west of UTC — a conference would read as starting a day early. Build
// the display from the date parts instead.
const dayParts = (d) => String(d).slice(0, 10).split('-').map(Number);
const fmtDay = (d, opts) => {
  if (!d) return '—';
  const [y, m, dd] = dayParts(d);
  if (!y || !m || !dd) return '—';
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, opts || { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtDayShort = (d) => fmtDay(d, { month: 'short', day: 'numeric' });
const sameDay = (a, b) => String(a).slice(0, 10) === String(b).slice(0, 10);

// Planning horizons. 12 weeks is the default first view — a quarter is the unit
// business development actually plans in, and it reaches far enough to show the
// next fiscal wall without compressing the near term into noise. 4 and 8 are the
// tighter working views; 26 is there for the long look.
const WINDOWS = [
  { key: 4,  label: '4 wk',  title: 'Four weeks — what is actually being worked now' },
  { key: 8,  label: '8 wk',  title: 'Eight weeks — the commitment horizon' },
  { key: 12, label: '12 wk', title: 'Twelve weeks — a quarter, the default planning forecast' },
  { key: 26, label: '26 wk', title: 'Twenty-six weeks — the long look, across a fiscal boundary' },
];

// Every stream carries a glyph, and a badge naming what the mark is. Colour is
// deliberately NOT used to separate streams: fill colour already means fiscal
// exposure, and adding a second colour channel would break the rule that
// identity never depends on hue.
const STREAM_GLYPH = {
  pipeline: '\u25C6', tusk: '\u25B2', gov_opportunity: '\u25A0',
  event: '\u25CF', internal: '\u25CB',
};
const STREAM_LABEL = {
  pipeline: 'Pipeline', tusk: 'Tusks', gov_opportunity: 'Gov Opportunities',
  event: 'Events', internal: 'Internal',
};
const PROJECTED_OUTLINE = '#64748B';

const laneHeight = (rows, overflow) => rows.length * 30 + 14 + ((overflow && overflow.length) ? 14 : 0);

const MARKER_STYLE = {
  hard:   { color: '#EF4444', band: '#EF444414' },
  risk:   { color: '#EA580C', band: '#EA580C14' },
  window: { color: '#c4a563', band: '#c4a5630F' },
  soft:   { color: '#64748B', band: '#64748B0F' },
};

// ── small shared bits ────────────────────────────────────────────────────────

const Chip = ({ children, color = C.dim, mono = false, title }) => (
  <span title={title} style={{
    padding: '1px 5px', borderRadius: 3, fontSize: 9, lineHeight: '14px',
    fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
    background: `${color}1A`, color, border: `1px solid ${color}44`, whiteSpace: 'nowrap',
  }}>{children}</span>
);

const Btn = ({ children, onClick, primary, disabled, small }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: small ? '3px 8px' : '5px 11px', fontSize: small ? 10 : 11, borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
    background: primary ? C.accent : 'transparent', color: primary ? '#04121A' : C.muted,
    border: `1px solid ${primary ? C.accent : C.border}`, fontWeight: primary ? 600 : 400,
  }}>{children}</button>
);

const Field = ({ label, children }) => (
  <label style={{ display: 'block', marginBottom: 8 }}>
    <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
    {children}
  </label>
);

const inputStyle = {
  width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4,
  color: C.text, fontSize: 11, padding: '5px 7px', boxSizing: 'border-box',
};

// ── the fiscal ruler ─────────────────────────────────────────────────────────

function FiscalRuler({ start, end, width, markers }) {
  const span = end - start;
  const pct = (d) => ((new Date(d) - start) / span) * 100;

  const months = [];
  const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  while (cur <= end) {
    if (cur >= start) months.push(new Date(cur));
    cur.setUTCMonth(cur.getUTCMonth() + 1);
  }

  return (
    <div style={{ position: 'relative', height: 62, width, borderBottom: `1px solid ${C.border}` }}>
      {months.map((m) => (
        <div key={m.toISOString()} style={{
          position: 'absolute', left: `${pct(m)}%`, top: 0, bottom: 0,
          borderLeft: `1px solid ${C.border}`, paddingLeft: 4,
        }}>
          <span style={{ fontSize: 9, color: C.faint, whiteSpace: 'nowrap' }}>
            {m.toLocaleDateString(undefined, { month: 'short' })}
            {m.getUTCMonth() === 9 && <span style={{ color: C.gold }}> FY{m.getUTCFullYear() + 1}</span>}
          </span>
        </div>
      ))}
      {/* Fiscal markers cluster around the fiscal year boundary, so their labels
          were printing on top of each other. Sort by position and drop each label
          to its own row when it would collide with the one before it. */}
      {(() => {
        const sorted = [...markers].sort((a, b) => pct(a.date_start) - pct(b.date_start));
        let lastEnd = -Infinity;
        let tier = 0;
        return sorted.map((mk) => {
          const st = MARKER_STYLE[mk.kind] || MARKER_STYLE.soft;
          const left = pct(mk.date_start);
          const right = mk.date_end ? pct(mk.date_end) : left;
          const isLine = !mk.date_end;
          // ~6.5px per character at 8px type, expressed as a share of the track
          const labelPct = ((String(mk.label).length * 5) / Math.max(1, width)) * 100;
          if (left < lastEnd) { tier = (tier + 1) % 3; } else { tier = 0; }
          lastEnd = left + labelPct;
          return (
            <div key={mk.id} title={`${mk.label}\n${mk.detail || ''}`} style={{
              position: 'absolute', left: `${Math.max(0, left)}%`,
              width: isLine ? 0 : `${Math.max(0.4, right - left)}%`,
              top: 18, bottom: 0,
              borderLeft: `${isLine ? 2 : 1}px ${mk.kind === 'risk' ? 'dashed' : 'solid'} ${st.color}`,
              background: isLine ? 'transparent' : st.band,
            }}>
              <span style={{
                position: 'absolute', left: 3, top: 1 + tier * 11, fontSize: 8, color: st.color,
                whiteSpace: 'nowrap', fontWeight: 600, maxWidth: 190,
                overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
              }}>{mk.label}</span>
            </div>
          );
        });
      })()}
    </div>
  );
}

// ── mark packing: greedily stack overlapping items into sub-rows ─────────────

// A mark occupies far more width than its glyph: the title and its badges sit to
// the right of it. Packing on the glyph alone is what let labels run through each
// other. Estimate the rendered label width and reserve THAT, so a colliding mark
// drops to the next sub-row instead of overprinting its neighbour.

const MAX_LABEL_PX = 250;   // titles are truncated to this, so packing can trust it
const CHAR_PX = 5.4;        // 10px Inter, measured average
const BADGE_PX = 52;

function labelWidthPx(it) {
  const badges =
    (it.badge ? BADGE_PX : 0) +
    (it.appropriation_code ? 44 : 0) +
    (it.authority_code ? 42 : 0) +
    (it.projected ? 58 : 0) +
    (it.needs_triage ? 44 : 0) +
    (it.over_ceiling ? 14 : 0);
  const title = Math.min(String(it.title || '').length * CHAR_PX, MAX_LABEL_PX);
  return 22 + title + badges;   // 22 = glyph + gaps
}

const MAX_ROWS_PER_LANE = 8;   // beyond this a lane stops being readable

function packLane(items, start, end, innerWidth) {
  const span = end - start;
  const pxToPct = (px) => (px / Math.max(1, innerWidth)) * 100;
  const rows = [];
  const sorted = [...items].sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const overflow = [];

  for (const it of sorted) {
    const l = ((new Date(it.start_at) - start) / span) * 100;
    const r = it.end_at ? ((new Date(it.end_at) - start) / span) * 100 : l;
    // A bar carries its label inside where it can, so it only needs the label
    // width beyond its own end when the bar is shorter than the text.
    const need = Math.max(r, l + pxToPct(labelWidthPx(it)));
    let placed = false;
    for (const row of rows) {
      if (row.lastRight <= l - 0.3) {
        row.items.push({ ...it, l, r }); row.lastRight = need; placed = true; break;
      }
    }
    if (!placed) {
      if (rows.length >= MAX_ROWS_PER_LANE) { overflow.push(it); continue; }
      rows.push({ items: [{ ...it, l, r }], lastRight: need });
    }
  }
  return { rows, overflow };
}

// ── one mark ─────────────────────────────────────────────────────────────────

function Mark({ e, onClick }) {
  const ex = e.exposure || {};
  // A projection is a date that lives on its own object and nobody has classified.
  // It gets an outline and no fill, because fill means fiscal exposure and an
  // unclassified date has no fiscal verdict to state. Pinning it fills it in.
  const projected = !!e.projected;
  const color = projected ? PROJECTED_OUTLINE : (ex.color || C.dim);
  const hatch = projected
    ? 'transparent'
    : (ex.hatch ? `repeating-linear-gradient(45deg, ${color}55 0 4px, ${color}18 4px 8px)` : `${color}33`);
  const outline = e.is_forecast ? 'dashed' : 'solid';

  const label = (
    <span style={{
      fontSize: 10, color: projected ? C.muted : C.text, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 4, paddingLeft: 6,
    }}>
      <span style={{ color: projected ? PROJECTED_OUTLINE : C.faint, fontSize: 9 }}
        title={STREAM_LABEL[e.stream] || e.stream}>{STREAM_GLYPH[e.stream] || ''}</span>
      <span style={{ maxWidth: MAX_LABEL_PX, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {e.title}
      </span>
      {e.badge && <Chip color={C.dim}>{String(e.badge).replace(/_/g, ' ')}</Chip>}
      {e.appropriation_code && <Chip mono color={C.muted} title="Appropriation — identity never depends on colour">{e.appropriation_code}</Chip>}
      {e.authority_code && <Chip color={C.accent} title={e.authority_cite || e.authority_label}>{e.authority_code}</Chip>}
      {e.over_ceiling && <span title="Exceeds the office warrant ceiling — routes upward, add weeks" style={{ color: '#EF4444', fontSize: 11 }}>⚑</span>}
      {e.needs_triage && <Chip color={C.dim} title="No acquisition authority or no appropriation assigned">triage</Chip>}
      {projected && <Chip color={PROJECTED_OUTLINE} title="Read-only — this date lives on its source record. Pin it to give it an owner and a colour of money.">projected</Chip>}
    </span>
  );

  if (e.shape === 'diamond') {
    return (
      <div onClick={() => onClick(e)} title={`${e.title} — ${fmtDate(e.start_at)}\n${ex.label}: ${ex.reason || ''}`}
        style={{ position: 'absolute', left: `${e.l}%`, top: 4, display: 'flex', alignItems: 'center', cursor: 'pointer', zIndex: 2 }}>
        <span style={{
          width: 11, height: 11, transform: 'rotate(45deg)',
          background: projected ? 'transparent' : (ex.hatch ? hatch : color),
          border: `1.5px ${outline} ${color}`, display: 'inline-block', flexShrink: 0,
          marginLeft: -5.5,
        }} />
        {label}
      </div>
    );
  }

  return (
    <div onClick={() => onClick(e)} title={`${e.title} — ${fmtDate(e.start_at)} to ${fmtDate(e.end_at)}\n${ex.label}: ${ex.reason || ''}`}
      style={{ position: 'absolute', left: `${e.l}%`, top: 5, display: 'flex', alignItems: 'center', cursor: 'pointer', zIndex: 2 }}>
      <div style={{
        width: `${Math.max(0.6, e.r - e.l)}%`, minWidth: 14, height: 13, background: hatch,
        border: `1.5px ${outline} ${color}`,
        borderRadius: e.clipped ? '3px 0 0 3px' : 3,
        borderRight: e.clipped ? `3px solid ${color}` : `1.5px ${outline} ${color}`,
        flexShrink: 0,
      }} />
      {label}
    </div>
  );
}

// ── the board ────────────────────────────────────────────────────────────────

function Board({ board, loading, onSelect, weeks, setWeeks, scope, setScope,
                streams, setStreams, groupBy, setGroupBy, govFilter, setGovFilter, onRefresh }) {
  const start = useMemo(() => new Date(board?.meta?.from || Date.now()), [board]);
  const end = useMemo(() => new Date(board?.meta?.to || Date.now()), [board]);
  const innerWidth = Math.max(900, weeks * 46);

  const byLane = useMemo(() => {
    if (!board) return [];
    return board.lanes.map((lane) => {
      const items = board.engagements.filter(e => e.lane_key === lane.lane_key);
      const { rows, overflow } = packLane(items, start, end, innerWidth);
      return { lane, rows, overflow };
    });
  }, [board, start, end, innerWidth]);

  const s = board?.summary;

  return (
    <div>
      {/* controls + summary */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {WINDOWS.map(w => (
            <Btn key={w.key} small primary={weeks === w.key} onClick={() => setWeeks(w.key)}>{w.label}</Btn>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['dod', 'civilian'].map(sc => (
            <Btn key={sc} small primary={scope === sc} onClick={() => setScope(sc)}>
              {sc === 'dod' ? 'DoD rhythm' : 'Civilian rhythm'}
            </Btn>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }} title="What forms the swimlane rows">
          <Btn small primary={groupBy === 'office'} onClick={() => setGroupBy('office')}>Lanes: office</Btn>
          <Btn small primary={groupBy === 'stream'} onClick={() => setGroupBy('stream')}>Lanes: stream</Btn>
        </div>
        {streams.includes('gov_opportunity') && (
          <Btn small primary={govFilter === 'all'}
            onClick={() => setGovFilter(govFilter === 'all' ? 'relevant' : 'all')}>
            {govFilter === 'all' ? 'All solicitations' : 'Relevant solicitations'}
          </Btn>
        )}
        <Btn small onClick={onRefresh}>Refresh</Btn>
        <div style={{ flex: 1 }} />
        {s && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 10, color: C.muted }}>
            <span><strong style={{ color: C.accent, fontSize: 12 }}>{fmtMoney(s.pipeline_value_usd)}</strong> in window</span>
            <span title="One opportunity produces several engagement records — this count is records, not pursuits">{s.engagement_count} records</span>
            {s.expiring_within_45_days > 0 && <span style={{ color: '#EF4444' }}>▲ {s.expiring_within_45_days} inside 45-day expiry</span>}
            {s.over_ceiling_count > 0 && <span style={{ color: '#EF4444' }}>⚑ {s.over_ceiling_count} over warrant ceiling</span>}
            {s.triage_count > 0 && <span style={{ color: C.gold }}>{s.triage_count} unclassified</span>}
          </div>
        )}
      </div>

      {/* stream filter — four kinds of scheduled activity on one board */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 2 }}>
          Streams
        </span>
        {['pipeline', 'tusk', 'gov_opportunity', 'event', 'internal'].map(k => {
          const on = streams.includes(k);
          const n = board?.summary?.by_stream?.[k];
          return (
            <span key={k} onClick={() => setStreams(on
                ? (streams.length > 1 ? streams.filter(x => x !== k) : streams)
                : [...streams, k])}
              title={board?.streams?.[k]?.note || ''}
              style={{
                cursor: 'pointer', userSelect: 'none', padding: '3px 9px', borderRadius: 12, fontSize: 10,
                border: `1px solid ${on ? C.accent : C.border}`,
                background: on ? `${C.accent}14` : 'transparent',
                color: on ? '#94E8F7' : C.faint,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
              <span style={{ fontSize: 9 }}>{STREAM_GLYPH[k]}</span>
              {STREAM_LABEL[k]}
              {n != null && <span style={{ color: on ? C.accent : C.faint, fontSize: 9 }}>{n}</span>}
            </span>
          );
        })}
        {board?.summary && (
          <span style={{ fontSize: 9, color: C.faint, marginLeft: 6 }}>
            {board.summary.owned_count} owned · {board.summary.projected_count} projected
            {board.meta?.gov_filter === 'relevant' && streams.includes('gov_opportunity') &&
              ' · solicitations filtered to capability matches and $1M+'}
          </span>
        )}
      </div>

      {/* legend — every state carries an icon and a text label, so colour never
          carries meaning alone */}
      {board && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, padding: '8px 10px', marginBottom: 10,
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 5 }}>
          <span style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Fill = fiscal exposure</span>
          {Object.values(board.legend).sort((a, b) => a.rank - b.rank).map(st => (
            <span key={st.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: C.muted }}>
              <span style={{
                width: 10, height: 10, borderRadius: 2, border: `1.5px solid ${st.color}`,
                background: st.hatch ? `repeating-linear-gradient(45deg, ${st.color}55 0 3px, ${st.color}18 3px 6px)` : `${st.color}33`,
              }} />
              <span style={{ color: st.color }}>{st.icon}</span> {st.label}
            </span>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 9, color: C.faint }}>
            ◇ point event · ▭ window · dashed = our forecast · squared end = runs past window · code = appropriation
            <br />outlined = projected from its source record, read-only until pinned · glyph = stream
          </span>
        </div>
      )}

      {loading && <div style={{ color: C.dim, fontSize: 12, padding: 30 }}>Loading the board…</div>}

      {!loading && board && board.lanes.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', border: `1px dashed ${C.border}`, borderRadius: 6 }}>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: 6 }}>No engagements in this window.</div>
          <div style={{ color: C.faint, fontSize: 11, maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
            Add an engagement against a pipeline opportunity, or promote an event from the
            Events tab onto the calendar. Lanes appear as soon as an engagement is assigned
            to a government office.
          </div>
        </div>
      )}

      {!loading && board && board.lanes.length > 0 && (
        <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden', background: C.panel }}>
          {/* lane headers */}
          <div style={{ width: 250, flexShrink: 0, borderRight: `1px solid ${C.border}` }}>
            <div style={{ height: 62, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-end', padding: '0 10px 6px' }}>
              <span style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Obligation authority</span>
            </div>
            {byLane.map(({ lane, rows, overflow }) => (
              <div key={lane.lane_key ?? 'none'} style={{
                height: laneHeight(rows, overflow), padding: '8px 10px',
                borderBottom: `1px solid ${C.border}`, overflow: 'hidden',
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {lane.group_by === 'stream' && <span style={{ color: C.faint, marginRight: 5, fontSize: 9 }}>{lane.glyph}</span>}
                  {lane.acronym || lane.name}
                </div>
                {lane.group_by === 'stream' && (
                  <div style={{ fontSize: 9, color: C.faint, marginTop: 3, lineHeight: 1.4 }}>
                    {lane.engagement_count} item{lane.engagement_count === 1 ? '' : 's'}
                    {lane.pipeline_value_usd > 0 && <span style={{ color: C.accent }}> · {fmtMoney(lane.pipeline_value_usd)}</span>}
                  </div>
                )}
                {lane.group_by !== 'stream' && <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
                  {lane.warrant_label
                    ? <Chip color={C.gold} title={lane.routing_note || lane.obligation_description}>{lane.warrant_label}</Chip>
                    : lane.is_department
                      ? <Chip color={C.faint} title="This is a department, not a contracting office. Nobody here signs — the solicitations in this lane have not been matched to the office that actually holds obligation authority.">department, not an office</Chip>
                      : <Chip color={C.faint} title="No warrant recorded on this organization. Set it on the org record to make the ceiling flag work.">warrant not set</Chip>}
                  <span style={{ fontSize: 9, color: C.accent }}>{fmtMoney(lane.pipeline_value_usd)}</span>
                </div>}
                {lane.over_ceiling_count > 0 && (
                  <div style={{ fontSize: 9, color: '#EF4444', marginTop: 2 }}>
                    ⚑ {lane.over_ceiling_count} above ceiling — routes upward
                  </div>
                )}
                {(lane.ko || lane.pm) && (
                  <div style={{ fontSize: 9, color: C.faint, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {lane.ko && <span title="Contracting officer — the person who can buy it">KO {lane.ko.name}</span>}
                    {lane.ko && lane.pm && ' · '}
                    {lane.pm && <span title="Program manager — the person who wants it">PM {lane.pm.name}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* timeline */}
          <div style={{ flex: 1, overflowX: 'auto' }}>
            <div style={{ width: innerWidth, position: 'relative' }}>
              <FiscalRuler start={start} end={end} width={innerWidth} markers={board.markers} />
              {byLane.map(({ lane, rows, overflow }) => (
                <div key={lane.lane_key ?? 'none'} style={{
                  position: 'relative', height: laneHeight(rows, overflow),
                  borderBottom: `1px solid ${C.border}`,
                }}>
                  {/* marker bands continue behind the lanes */}
                  {board.markers.map((mk) => {
                    const st = MARKER_STYLE[mk.kind] || MARKER_STYLE.soft;
                    const span = end - start;
                    const l = ((new Date(mk.date_start) - start) / span) * 100;
                    const r = mk.date_end ? ((new Date(mk.date_end) - start) / span) * 100 : l;
                    return (
                      <div key={mk.id} style={{
                        position: 'absolute', left: `${Math.max(0, l)}%`,
                        width: mk.date_end ? `${Math.max(0.3, r - l)}%` : 0, top: 0, bottom: 0,
                        borderLeft: `${mk.date_end ? 1 : 2}px ${mk.kind === 'risk' ? 'dashed' : 'solid'} ${st.color}66`,
                        background: mk.date_end ? st.band : 'transparent', pointerEvents: 'none',
                      }} />
                    );
                  })}
                  {rows.map((row, ri) => (
                    <div key={ri} style={{ position: 'absolute', left: 0, right: 0, top: ri * 30 + 5, height: 26 }}>
                      {row.items.map(e => <Mark key={e.id} e={e} onClick={onSelect} />)}
                    </div>
                  ))}
                  {overflow.length > 0 && (
                    <div style={{
                      position: 'absolute', left: 8, top: rows.length * 30 + 6, fontSize: 9, color: C.faint,
                    }}>
                      + {overflow.length} more in this lane — narrow the window or filter a stream out to see them
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {board?.meta && (
        <div style={{ marginTop: 8, fontSize: 9, color: C.faint }}>
          {fmtDate(board.meta.from)} – {fmtDate(board.meta.to)} · FY{board.meta.fiscal_year} closes {fmtDate(board.meta.fy_close)} ·
          {' '}{scope === 'dod' ? 'DoD' : 'civilian'} fiscal rhythm · CR windows shown are planning assumptions, not fact
        </div>
      )}
    </div>
  );
}

// ── contact picker (used by engagement attendees and event targets) ──────────

function ContactPicker({ onPick, placeholder = 'Search contacts…' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    let live = true;
    const t = setTimeout(() => {
      crmService.getContacts({ search: q, limit: 12 })
        .then(r => { if (live) setResults(asList(r, 'contacts')); })
        .catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  return (
    <div style={{ position: 'relative' }}>
      <input style={inputStyle} value={q} placeholder={placeholder} onChange={e => setQ(e.target.value)} />
      {results.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 30, left: 0, right: 0, top: '100%', maxHeight: 200,
          overflowY: 'auto', background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4,
        }}>
          {results.map(c => (
            <div key={c.id} onClick={() => { onPick(c); setQ(''); setResults([]); }}
              style={{ padding: '5px 8px', fontSize: 11, color: C.text, cursor: 'pointer', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.accent }}>{c.rank_code || c.grade_code || ''}</span> {c.first_name} {c.last_name}
              <span style={{ color: C.faint, fontSize: 9, marginLeft: 5 }}>{c.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The API returns bare arrays in some places and {key: []} in others.
function asList(payload, key) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload[key])) return payload[key];
  if (payload && Array.isArray(payload.rows)) return payload.rows;
  return [];
}

// ── engagement drawer ────────────────────────────────────────────────────────

function EngagementDrawer({ id, refs, onClose, onChanged }) {
  const [e, setE] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    crmService.getEngagement(id).then(setE).catch(() => setE(null));
  }, [id]);
  useEffect(load, [load]);

  if (!e) return null;
  const ex = e.exposure || {};

  const patch = async (body) => {
    setSaving(true);
    try { await crmService.updateEngagement(id, body); load(); onChanged?.(); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: '#00000099', zIndex: 100, display: 'flex', justifyContent: 'flex-end',
    }}>
      <div onClick={ev => ev.stopPropagation()} style={{
        width: 460, maxWidth: '92vw', height: '100%', overflowY: 'auto',
        background: C.panel, borderLeft: `1px solid ${C.border}`, padding: 18,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{e.title}</div>
            <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
              {e.type.replace(/_/g, ' ')} · {fmtDate(e.start_at)}{e.end_at ? ` → ${fmtDate(e.end_at)}` : ' (point event)'}
            </div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        {/* exposure — derived, with its reasoning shown so nobody has to trust a colour */}
        <div style={{
          marginTop: 14, padding: '9px 11px', borderRadius: 5,
          background: `${ex.color}14`, border: `1px solid ${ex.color}55`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: ex.color }}>{ex.icon} {ex.label}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>{ex.reason}</div>
          {ex.days_to_expiry != null && (
            <div style={{ fontSize: 10, color: ex.in_45_day_window ? '#EF4444' : C.dim, marginTop: 3 }}>
              {ex.days_to_expiry} days to expiry{ex.in_45_day_window ? ' — inside the 45-day window' : ''}
            </div>
          )}
          <div style={{ fontSize: 9, color: C.faint, marginTop: 5 }}>
            Derived every time this loads, never typed. Change the appropriation to change this.
          </div>
        </div>

        {e.over_ceiling && (
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 5, background: '#EF444414', border: '1px solid #EF444455' }}>
            <div style={{ fontSize: 11, color: '#EF4444', fontWeight: 600 }}>⚑ Above the office warrant ceiling</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
              {fmtMoney(e.opportunity_value_usd ?? e.value_usd)} against a {fmtMoney(e.warrant_ceiling_usd)} warrant.
              {e.routing_note ? ` ${e.routing_note}` : ' This routes upward — add weeks to the timeline.'}
            </div>
          </div>
        )}

        {/* pipeline tie */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Pipeline</div>
          {e.opportunity_id ? (
            <div style={{ fontSize: 11, color: C.text }}>
              {e.opportunity_name}
              <span style={{ color: C.accent, marginLeft: 8 }}>{fmtMoney(e.opportunity_value_usd)}</span>
              <span style={{ color: C.faint, marginLeft: 8, fontSize: 10 }}>{e.opportunity_stage}</span>
            </div>
          ) : (
            <div style={{ fontSize: 10, color: C.faint }}>
              Not tied to an opportunity. Roll-ups count this record on its own.
            </div>
          )}
          {e.event_name && (
            <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
              From event: {e.event_website ? <a href={e.event_website} target="_blank" rel="noreferrer" style={{ color: C.accent }}>{e.event_name}</a> : e.event_name}
            </div>
          )}
        </div>

        {/* classification — the triage fields */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Classification
          </div>
          <Field label="Government office (lane)">
            <select style={inputStyle} value={e.organization_id || ''} onChange={ev => patch({ organization_id: ev.target.value || null })}>
              <option value="">— unassigned —</option>
              {refs.offices.map(o => <option key={o.id} value={o.id}>{o.acronym ? `${o.acronym} — ` : ''}{o.name}</option>)}
            </select>
          </Field>
          <Field label="Acquisition authority">
            <select style={inputStyle} value={e.authority_id || ''} onChange={ev => patch({ authority_id: ev.target.value || null })}>
              <option value="">— not assigned —</option>
              {refs.authorities.map(a => <option key={a.id} value={a.id}>{a.code} — {a.label}</option>)}
            </select>
          </Field>
          {e.authority_cite && (
            <div style={{ fontSize: 9, color: C.faint, marginTop: -4, marginBottom: 8 }}>
              {e.authority_cite}{e.cycle_note ? ` · ${e.cycle_note}` : ''}
            </div>
          )}
          <Field label="Appropriation">
            <select style={inputStyle} value={e.appropriation_id || ''} onChange={ev => patch({ appropriation_id: ev.target.value || null })}>
              <option value="">— not assigned —</option>
              {refs.appropriations.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name} ({a.life})</option>)}
            </select>
          </Field>
          <Field label="Appropriation fiscal year">
            <input type="number" style={inputStyle} defaultValue={e.appropriation_fy || ''}
              onBlur={ev => ev.target.value !== String(e.appropriation_fy || '') && patch({ appropriation_fy: ev.target.value || null })} />
          </Field>
          <Field label="Status">
            <select style={inputStyle} value={e.status} onChange={ev => patch({ status: ev.target.value })}>
              {['forecast', 'confirmed', 'at_risk', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </Field>
          {e.status === 'forecast' && (
            <div style={{ fontSize: 9, color: C.faint, marginTop: -4, marginBottom: 8 }}>
              Forecast dates render with a dashed outline — this is our date, not the government's.
            </div>
          )}
        </div>

        {/* contacts */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Contacts ({e.contacts?.length || 0})
          </div>
          {(e.contacts || []).map(c => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
              borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.text,
            }}>
              <span style={{ flex: 1 }}>
                <span style={{ color: C.accent }}>{c.rank_code || c.grade_code || ''}</span> {c.first_name} {c.last_name}
                <span style={{ color: C.faint, fontSize: 9, marginLeft: 5 }}>{c.title}</span>
              </span>
              <Chip color={C.muted}>{c.role?.replace(/_/g, ' ')}</Chip>
              <Chip color={c.attendance === 'attended' ? '#059669' : C.dim}>{c.attendance}</Chip>
              <span onClick={async () => { await crmService.removeEngagementContact(id, c.contact_id); load(); }}
                style={{ cursor: 'pointer', color: C.faint, fontSize: 12 }} title="Remove">×</span>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <ContactPicker placeholder="Add a contact to this engagement…" onPick={async (c) => {
              await crmService.addEngagementContact(id, { contact_id: c.id, role: 'attendee', attendance: 'invited' });
              load();
            }} />
          </div>
        </div>

        {e.detail && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Detail</div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{e.detail}</div>
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Btn small primary disabled={busy === 'log'} onClick={async () => {
            setBusy('log');
            try { await crmService.logEngagementInteraction(id, {}); onChanged?.(); }
            finally { setBusy(''); }
          }}>Log as interaction</Btn>
          <Btn small onClick={async () => {
            if (!window.confirm('Delete this engagement?')) return;
            await crmService.deleteEngagement(id); onChanged?.(); onClose();
          }}>Delete</Btn>
          {saving && <span style={{ fontSize: 10, color: C.dim, alignSelf: 'center' }}>saving…</span>}
        </div>
        <div style={{ marginTop: 10, fontSize: 9, color: C.faint }}>
          Source: {e.source}{e.confirmed_by ? ` · confirmed by ${e.confirmed_by}` : ''}
        </div>
      </div>
    </div>
  );
}

// ── projection drawer ────────────────────────────────────────────────────────
// A projected mark is a date owned by another record. There is nothing to edit
// here — editing it means editing the opportunity, the solicitation or the TRL
// gate it came from. What you CAN do is pin it, which creates a real engagement
// beside it so the date can carry an owner, an acquisition authority and a
// colour of money that its source object has no field for.

const PIN_KIND = {
  crm_opportunities: 'opportunity',
  crm_gov_opportunities: 'gov_opportunity',
  crm_tech_milestones: 'milestone',
  crm_events: 'event',
};

function ProjectionDrawer({ mark: m, onClose, onPinned }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const kind = PIN_KIND[m.source_ref?.table];

  const pin = async () => {
    setBusy(true); setErr('');
    try {
      if (kind === 'event') {
        await crmService.promoteEvent(m.source_ref.id, {});
      } else {
        await crmService.pinToCalendar({ kind, ref_id: m.source_ref.id, field: m.pin_field });
      }
      onPinned(); onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: '#00000099', zIndex: 100, display: 'flex', justifyContent: 'flex-end',
    }}>
      <div onClick={ev => ev.stopPropagation()} style={{
        width: 430, maxWidth: '92vw', height: '100%', overflowY: 'auto',
        background: C.panel, borderLeft: `1px solid ${C.border}`, padding: 18,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{m.title}</div>
            <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
              <span style={{ marginRight: 5 }}>{STREAM_GLYPH[m.stream]}</span>
              {STREAM_LABEL[m.stream]} · {fmtDate(m.start_at)}
              {m.end_at ? ` → ${fmtDate(m.end_at)}` : ''}
            </div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        <div style={{
          marginTop: 14, padding: '9px 11px', borderRadius: 5,
          background: `${PROJECTED_OUTLINE}14`, border: `1px solid ${PROJECTED_OUTLINE}55`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>Projected — read-only</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 1.6 }}>
            This date lives on {({
              crm_opportunities: 'the opportunity record',
              crm_gov_opportunities: 'the SAM.gov solicitation',
              crm_tech_milestones: 'the TRL milestone',
              crm_events: 'the event record',
            })[m.source_ref?.table] || 'its source record'}
            {m.pin_field ? ` as ${m.pin_field.replace(/_/g, ' ')}` : ''}. Change it there and the board follows —
            there is no second copy to fall out of step.
          </div>
        </div>

        <div style={{ marginTop: 14, fontSize: 11, color: C.muted, lineHeight: 1.8 }}>
          {m.subtitle && <div>{m.subtitle}</div>}
          {m.office_name && <div>Office: {m.office_name}</div>}
          {m.value_usd ? <div>Value: <span style={{ color: C.accent }}>{fmtMoney(m.value_usd)}</span></div> : null}
          {m.badge && <div>Type: {String(m.badge).replace(/_/g, ' ')}</div>}
          {m.detail && <div style={{ color: C.faint, marginTop: 6 }}>{m.detail}</div>}
        </div>

        <div style={{ marginTop: 20, padding: '10px 12px', borderRadius: 5, border: `1px dashed ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.text, fontWeight: 600, marginBottom: 4 }}>Pin to the calendar</div>
          <div style={{ fontSize: 10, color: C.faint, lineHeight: 1.6, marginBottom: 9 }}>
            Creates an engagement linked to this record. The projection is replaced by the pinned
            row, so the date is still drawn once — but it can now carry an owner, an acquisition
            authority, an appropriation, and therefore a fiscal exposure.
          </div>
          {err && <div style={{ color: '#EF4444', fontSize: 10, marginBottom: 7 }}>{err}</div>}
          <Btn small primary disabled={busy || !kind} onClick={pin}>
            {busy ? 'Pinning…' : 'Pin to calendar'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── new engagement ───────────────────────────────────────────────────────────

const ENG_TYPES = ['meeting', 'industry_day', 'site_visit', 'conference', 'window', 'decision', 'pop', 'proposal_due', 'award', 'travel', 'deadline', 'other'];

function NewEngagement({ refs, opportunities, subsidiaries, prefill, onClose, onCreated }) {
  const [f, setF] = useState({
    title: '', type: 'meeting', organization_id: '', opportunity_id: '', subsidiary_id: '',
    start_at: iso(new Date()), end_at: '', status: 'forecast', owner: '', location: '',
    authority_id: '', appropriation_id: '', next_action: '', detail: '', ...prefill,
  });
  const [contacts, setContacts] = useState([]);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!f.title || !f.start_at) { setErr('Title and start date are required.'); return; }
    try {
      const body = {
        ...f,
        start_at: new Date(`${f.start_at}T09:00:00`).toISOString(),
        end_at: f.end_at ? new Date(`${f.end_at}T17:00:00`).toISOString() : null,
        organization_id: f.organization_id || null,
        opportunity_id: f.opportunity_id || null,
        subsidiary_id: f.subsidiary_id || null,
        authority_id: f.authority_id || null,
        appropriation_id: f.appropriation_id || null,
        contacts: contacts.map(c => ({ contact_id: c.id, role: 'attendee', attendance: 'invited' })),
      };
      await crmService.createEngagement(body);
      onCreated(); onClose();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000000AA', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 520, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto',
        background: C.panel, border: `1px solid ${C.border}`, borderRadius: 7, padding: 18,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12 }}>New engagement</div>

        <Field label="Title"><input style={inputStyle} value={f.title} onChange={set('title')} placeholder="AMC Industry Day — counter-UAS" /></Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Type">
            <select style={inputStyle} value={f.type} onChange={set('type')}>
              {ENG_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select style={inputStyle} value={f.status} onChange={set('status')}>
              {['forecast', 'confirmed', 'at_risk'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="Start"><input type="date" style={inputStyle} value={f.start_at} onChange={set('start_at')} /></Field>
          <Field label="End (blank = point event)"><input type="date" style={inputStyle} value={f.end_at} onChange={set('end_at')} /></Field>
        </div>
        <div style={{ fontSize: 9, color: C.faint, marginTop: -4, marginBottom: 10 }}>
          Leave the end date blank for a moment in time — it renders as a diamond. A one-hour
          meeting drawn as a one-day bar misrepresents the data.
        </div>

        <Field label="Government office — the lane, and who can obligate">
          <select style={inputStyle} value={f.organization_id} onChange={set('organization_id')}>
            <option value="">— unassigned (goes to triage) —</option>
            {refs.offices.map(o => <option key={o.id} value={o.id}>{o.acronym ? `${o.acronym} — ` : ''}{o.name}</option>)}
          </select>
        </Field>

        <Field label="Pipeline opportunity">
          <select style={inputStyle} value={f.opportunity_id} onChange={set('opportunity_id')}>
            <option value="">— none —</option>
            {opportunities.map(o => <option key={o.id} value={o.id}>{o.name} ({fmtMoney(o.value_usd)})</option>)}
          </select>
        </Field>
        <div style={{ fontSize: 9, color: C.faint, marginTop: -4, marginBottom: 10 }}>
          One opportunity carries several engagements. Roll-ups de-duplicate on the opportunity,
          so linking here is what keeps the dollar figure honest.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Acquisition authority">
            <select style={inputStyle} value={f.authority_id} onChange={set('authority_id')}>
              <option value="">— not assigned —</option>
              {refs.authorities.map(a => <option key={a.id} value={a.id}>{a.code}</option>)}
            </select>
          </Field>
          <Field label="Appropriation">
            <select style={inputStyle} value={f.appropriation_id} onChange={set('appropriation_id')}>
              <option value="">— not assigned —</option>
              {refs.appropriations.map(a => <option key={a.id} value={a.id}>{a.code}</option>)}
            </select>
          </Field>
          <Field label="Subsidiary">
            <select style={inputStyle} value={f.subsidiary_id} onChange={set('subsidiary_id')}>
              <option value="">— none —</option>
              {subsidiaries.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Owner"><input style={inputStyle} value={f.owner} onChange={set('owner')} /></Field>
        </div>

        <Field label="Location"><input style={inputStyle} value={f.location} onChange={set('location')} /></Field>
        <Field label="Next action"><input style={inputStyle} value={f.next_action} onChange={set('next_action')} /></Field>
        <Field label="Detail"><textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={f.detail} onChange={set('detail')} /></Field>

        <Field label="Contacts">
          <ContactPicker onPick={c => setContacts(s => s.some(x => x.id === c.id) ? s : [...s, c])} />
        </Field>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
          {contacts.map(c => (
            <Chip key={c.id} color={C.accent}>
              {c.last_name} <span onClick={() => setContacts(s => s.filter(x => x.id !== c.id))} style={{ cursor: 'pointer' }}>×</span>
            </Chip>
          ))}
        </div>

        {err && <div style={{ color: '#EF4444', fontSize: 11, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn primary onClick={submit}>Create</Btn>
        </div>
      </div>
    </div>
  );
}

// ── events ───────────────────────────────────────────────────────────────────

const EVENT_TYPES = ['conference', 'trade_show', 'symposium', 'industry_day', 'pre_solicitation', 'matchmaking', 'demo_day', 'pitch_event', 'training', 'hearing', 'site_visit', 'other'];
const HOST_TYPES = ['government', 'industry_association', 'commercial', 'academic', 'other'];
const PARTICIPATION = ['considering', 'registered', 'attending', 'exhibiting', 'speaking', 'sponsoring', 'declined', 'attended', 'no_show'];

const PARTICIPATION_COLOR = {
  considering: '#64748B', registered: '#3B82F6', attending: '#22D3EE', exhibiting: '#c4a563',
  speaking: '#8B5CF6', sponsoring: '#c4a563', declined: '#4B5563', attended: '#059669', no_show: '#EF4444',
};

function EventRow({ ev, onOpen }) {
  const posture = (ev.attendance || [])[0];
  return (
    <div onClick={() => onOpen(ev)} style={{
      display: 'grid', gridTemplateColumns: '92px 1fr 150px 120px 100px', gap: 10, alignItems: 'center',
      padding: '9px 12px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', fontSize: 11,
    }}>
      <div>
        <div style={{ color: C.accent, fontWeight: 600 }}>{fmtDayShort(ev.start_date)}</div>
        {ev.end_date && !sameDay(ev.end_date, ev.start_date) && (
          <div style={{ color: C.faint, fontSize: 9 }}>→ {fmtDayShort(ev.end_date)}</div>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: C.text, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {ev.acronym ? `${ev.acronym} — ` : ''}{ev.name}
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip color={ev.host_type === 'government' ? '#059669' : C.dim}>{ev.event_type.replace(/_/g, ' ')}</Chip>
          {ev.host_name && <span style={{ color: C.faint, fontSize: 9 }}>{ev.host_name}</span>}
          {!ev.verified && <Chip color={C.gold} title="Proposed by an automated source. Confirm before committing budget.">unverified</Chip>}
          {Number(ev.target_count) > 0 && <Chip color={C.accent}>{ev.target_count} targets</Chip>}
        </div>
      </div>
      <div style={{ color: C.muted, fontSize: 10 }}>
        {ev.is_virtual ? 'Virtual' : [ev.location_city, ev.location_state].filter(Boolean).join(', ') || '—'}
        {ev.venue && <div style={{ color: C.faint, fontSize: 9 }}>{ev.venue}</div>}
      </div>
      <div style={{ fontSize: 10, color: C.muted }}>
        {ev.registration_deadline && <div>Reg by {fmtDayShort(ev.registration_deadline)}</div>}
        {ev.exhibit_cost_usd && <div style={{ color: C.faint, fontSize: 9 }}>Booth {fmtMoney(ev.exhibit_cost_usd)}</div>}
      </div>
      <div>
        {posture
          ? <Chip color={PARTICIPATION_COLOR[posture.participation] || C.dim}>{posture.participation}</Chip>
          : <span style={{ color: C.faint, fontSize: 10 }}>no decision</span>}
      </div>
    </div>
  );
}

function EventDrawer({ id, opportunities, subsidiaries, onClose, onChanged }) {
  const [ev, setEv] = useState(null);
  const load = useCallback(() => { crmService.getEvent(id).then(setEv).catch(() => setEv(null)); }, [id]);
  useEffect(load, [load]);
  const [posture, setPosture] = useState({});

  if (!ev) return null;
  const mine = (ev.attendance || [])[0];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#00000099', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 470, maxWidth: '92vw', height: '100%', overflowY: 'auto',
        background: C.panel, borderLeft: `1px solid ${C.border}`, padding: 18,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{ev.name}</div>
            <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
              {fmtDay(ev.start_date)}{ev.end_date && !sameDay(ev.end_date, ev.start_date) ? ` → ${fmtDay(ev.end_date)}` : ''}
              {' · '}{ev.event_type.replace(/_/g, ' ')}
            </div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        {!ev.verified && (
          <div style={{ marginTop: 12, padding: '8px 10px', borderRadius: 5, background: `${C.gold}14`, border: `1px solid ${C.gold}55` }}>
            <div style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>Unverified — proposed by {ev.source.replace('_', '.')}</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
              {ev.notes || 'Confirm the date, venue and cost against the organiser before committing budget or briefing it.'}
            </div>
            <div style={{ marginTop: 7 }}>
              <Btn small primary onClick={async () => { await crmService.verifyEvent(id); load(); onChanged?.(); }}>Confirm this event</Btn>
            </div>
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 11, color: C.muted, lineHeight: 1.7 }}>
          {ev.host_name && <div>Host: {ev.host_org_name || ev.host_name}</div>}
          <div>Where: {ev.is_virtual ? 'Virtual' : [ev.venue, ev.location_city, ev.location_state].filter(Boolean).join(', ') || '—'}</div>
          {ev.registration_deadline && <div>Registration closes {fmtDay(ev.registration_deadline)}</div>}
          {ev.abstract_deadline && <div>Abstracts due {fmtDay(ev.abstract_deadline)}</div>}
          {(ev.registration_cost_usd || ev.exhibit_cost_usd) && (
            <div>Cost: {fmtMoney(ev.registration_cost_usd)} attend · {fmtMoney(ev.exhibit_cost_usd)} exhibit</div>
          )}
          {ev.classification_level && ev.classification_level !== 'unclassified' && (
            <div style={{ color: C.gold }}>Classification: {ev.classification_level.toUpperCase()}</div>
          )}
          {ev.website && <div><a href={ev.website} target="_blank" rel="noreferrer" style={{ color: C.accent }}>{ev.website}</a></div>}
        </div>

        {ev.description && (
          <div style={{ marginTop: 12, fontSize: 10, color: C.faint, lineHeight: 1.6, maxHeight: 130, overflowY: 'auto' }}>
            {ev.description}
          </div>
        )}

        {/* our posture */}
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Our posture</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Participation">
              <select style={inputStyle} defaultValue={mine?.participation || 'considering'}
                onChange={e => setPosture(p => ({ ...p, participation: e.target.value }))}>
                {PARTICIPATION.map(p => <option key={p} value={p}>{p.replace('_', ' ')}</option>)}
              </select>
            </Field>
            <Field label="Subsidiary">
              <select style={inputStyle} defaultValue={mine?.subsidiary_id || ''}
                onChange={e => setPosture(p => ({ ...p, subsidiary_id: e.target.value || null }))}>
                <option value="">— none —</option>
                {subsidiaries.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Decision due">
              <input type="date" style={inputStyle} defaultValue={mine?.decision_due?.slice(0, 10) || ''}
                onChange={e => setPosture(p => ({ ...p, decision_due: e.target.value || null }))} />
            </Field>
            <Field label="Cost committed">
              <input type="number" style={inputStyle} defaultValue={mine?.cost_usd || ''}
                onChange={e => setPosture(p => ({ ...p, cost_usd: e.target.value || null }))} />
            </Field>
          </div>
          <Field label="Objectives — what we are trying to get out of it">
            <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} defaultValue={mine?.objectives || ''}
              onChange={e => setPosture(p => ({ ...p, objectives: e.target.value }))} />
          </Field>
          <Btn small primary onClick={async () => {
            await crmService.setEventAttendance(id, { subsidiary_id: mine?.subsidiary_id || null, ...mine, ...posture });
            load(); onChanged?.();
          }}>Save posture</Btn>
        </div>

        {/* targets — the opportunity-discovery record */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Who we are hunting ({ev.targets?.length || 0})
          </div>
          <div style={{ fontSize: 9, color: C.faint, marginBottom: 7, lineHeight: 1.5 }}>
            A conference is worth its cost when there is a named person to find. List them before
            you go, mark them met after.
          </div>
          {(ev.targets || []).map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
              <span style={{ flex: 1, color: C.text }}>
                {t.contact_id
                  ? <>{t.rank_code || t.grade_code || ''} {t.first_name} {t.last_name}<span style={{ color: C.faint, fontSize: 9, marginLeft: 5 }}>{t.title}</span></>
                  : t.organization_name || t.opportunity_name || 'Target'}
                {t.objective && <div style={{ color: C.faint, fontSize: 9 }}>{t.objective}</div>}
              </span>
              <select style={{ ...inputStyle, width: 90, padding: '2px 4px', fontSize: 10 }} defaultValue={t.status}
                onChange={async e => { await crmService.updateEventTarget(t.id, { status: e.target.value }); load(); }}>
                {['planned', 'met', 'missed', 'deferred'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span onClick={async () => { await crmService.deleteEventTarget(t.id); load(); }}
                style={{ cursor: 'pointer', color: C.faint }}>×</span>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <ContactPicker placeholder="Add a person to find at this event…" onPick={async (c) => {
              await crmService.addEventTarget(id, { contact_id: c.id, organization_id: c.organization_id || null });
              load();
            }} />
          </div>
        </div>

        {/* on the calendar */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            On the planning calendar
          </div>
          {(ev.engagements || []).length > 0 ? (
            (ev.engagements || []).map(g => (
              <div key={g.id} style={{ fontSize: 11, color: C.muted, padding: '3px 0' }}>
                {g.title} · {fmtDate(g.start_at)} · {g.status}
              </div>
            ))
          ) : (
            <div style={{ fontSize: 10, color: C.faint, marginBottom: 8 }}>
              Not yet on the board. Promoting it creates an engagement so this event sits in the
              same swimlane as everything else, against an opportunity if you pick one.
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
            <select id="promote-opp" style={{ ...inputStyle, width: 220 }} defaultValue="">
              <option value="">— no opportunity —</option>
              {opportunities.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <Btn small primary onClick={async () => {
              const sel = document.getElementById('promote-opp');
              await crmService.promoteEvent(id, { opportunity_id: sel?.value || null });
              load(); onChanged?.();
            }}>Add to calendar</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── events tab ───────────────────────────────────────────────────────────────

function EventsTab({ opportunities, subsidiaries, isHQ }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event_type: '', host_type: '', q: '', upcoming: 'true' });
  const [openId, setOpenId] = useState(null);
  const [sources, setSources] = useState(null);
  const [msg, setMsg] = useState('');
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    crmService.getEvents(filters)
      .then(r => setEvents(asList(r, 'events')))
      .catch(e => setMsg(e.message))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(load, [load]);
  useEffect(() => { crmService.getEventSourceStatus().then(setSources).catch(() => {}); }, []);

  const unverified = events.filter(e => !e.verified).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search events…"
          value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} />
        <select style={{ ...inputStyle, width: 150 }} value={filters.event_type}
          onChange={e => setFilters(f => ({ ...f, event_type: e.target.value }))}>
          <option value="">All types</option>
          {EVENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={{ ...inputStyle, width: 160 }} value={filters.host_type}
          onChange={e => setFilters(f => ({ ...f, host_type: e.target.value }))}>
          <option value="">All hosts</option>
          {HOST_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <label style={{ fontSize: 10, color: C.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={filters.upcoming === 'true'}
            onChange={e => setFilters(f => ({ ...f, upcoming: e.target.checked ? 'true' : '' }))} />
          Upcoming only
        </label>
        <div style={{ flex: 1 }} />
        <Btn small primary onClick={() => setShowNew(true)}>+ Add event</Btn>
        {isHQ && (
          <Btn small onClick={async () => {
            setMsg('Pulling SAM.gov…');
            try {
              const r = await crmService.syncSamEvents({ lookback_days: 30 });
              setMsg(r.skipped ? r.skipped : `SAM.gov: ${r.added} added, ${r.updated} updated, ${r.scanned} notices scanned.`);
              load();
            } catch (e) { setMsg(e.message); }
          }}>Sync SAM.gov</Btn>
        )}
        {isHQ && sources?.conference_ingest?.enabled && (
          <Btn small onClick={async () => {
            const url = window.prompt('Conference page URL (https). Reads schema.org event data.');
            if (!url) return;
            setMsg('Reading…');
            try {
              const r = await crmService.ingestEventSite({ url });
              setMsg(r.error ? r.error : `Found ${r.found} events, added ${r.added} for review.`);
              load();
            } catch (e) { setMsg(e.message); }
          }}>Ingest site</Btn>
        )}
      </div>

      {sources && (
        <div style={{ fontSize: 9, color: C.faint, marginBottom: 10 }}>
          Sources — SAM.gov (.gov, {sources.sam_gov.enabled ? 'configured' : 'no API key'}) ·
          {' '}conference-site ingest ({sources.conference_ingest.enabled ? 'enabled' : 'off — non-.gov carve-out, set EVENT_INGEST_ENABLED'})
          {' '}· everything automated lands unverified
        </div>
      )}

      {msg && <div style={{ fontSize: 11, color: C.accent, marginBottom: 10 }}>{msg}</div>}

      {unverified > 0 && (
        <div style={{ padding: '7px 11px', marginBottom: 10, borderRadius: 5, background: `${C.gold}12`, border: `1px solid ${C.gold}44`, fontSize: 11, color: C.gold }}>
          {unverified} event{unverified === 1 ? '' : 's'} proposed by an automated source and not yet confirmed.
          Dates from a machine are a starting point, not a briefing.
        </div>
      )}

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: C.panel, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '92px 1fr 150px 120px 100px', gap: 10,
          padding: '7px 12px', borderBottom: `1px solid ${C.border}`,
          fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <div>Date</div><div>Event</div><div>Where</div><div>Deadlines</div><div>Posture</div>
        </div>
        {loading && <div style={{ padding: 24, color: C.dim, fontSize: 12 }}>Loading…</div>}
        {!loading && events.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: C.faint, fontSize: 11, lineHeight: 1.7 }}>
            No events yet.<br />
            Add one by hand, or pull government industry days from SAM.gov.
          </div>
        )}
        {!loading && events.map(ev => <EventRow key={ev.id} ev={ev} onOpen={e => setOpenId(e.id)} />)}
      </div>

      {openId && (
        <EventDrawer id={openId} opportunities={opportunities} subsidiaries={subsidiaries}
          onClose={() => setOpenId(null)} onChanged={load} />
      )}
      {showNew && <NewEvent onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}

function NewEvent({ onClose, onCreated }) {
  const [f, setF] = useState({
    name: '', acronym: '', event_type: 'conference', host_type: 'industry_association',
    host_name: '', start_date: iso(new Date()), end_date: '', venue: '', location_city: '',
    location_state: '', website: '', registration_deadline: '', registration_cost_usd: '',
    exhibit_cost_usd: '', description: '', recurrence: 'annual',
  });
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000000AA', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 520, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto',
        background: C.panel, border: `1px solid ${C.border}`, borderRadius: 7, padding: 18,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12 }}>Add an event</div>
        <Field label="Name"><input style={inputStyle} value={f.name} onChange={set('name')} placeholder="AUSA Annual Meeting & Exposition" /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Acronym"><input style={inputStyle} value={f.acronym} onChange={set('acronym')} /></Field>
          <Field label="Host"><input style={inputStyle} value={f.host_name} onChange={set('host_name')} placeholder="AUSA" /></Field>
          <Field label="Type">
            <select style={inputStyle} value={f.event_type} onChange={set('event_type')}>
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
          <Field label="Host type">
            <select style={inputStyle} value={f.host_type} onChange={set('host_type')}>
              {HOST_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
          <Field label="Start"><input type="date" style={inputStyle} value={f.start_date} onChange={set('start_date')} /></Field>
          <Field label="End"><input type="date" style={inputStyle} value={f.end_date} onChange={set('end_date')} /></Field>
          <Field label="City"><input style={inputStyle} value={f.location_city} onChange={set('location_city')} /></Field>
          <Field label="State"><input style={inputStyle} value={f.location_state} onChange={set('location_state')} /></Field>
          <Field label="Registration closes"><input type="date" style={inputStyle} value={f.registration_deadline} onChange={set('registration_deadline')} /></Field>
          <Field label="Recurrence">
            <select style={inputStyle} value={f.recurrence} onChange={set('recurrence')}>
              {['annual', 'semiannual', 'quarterly', 'one_time', 'irregular'].map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="Attend cost"><input type="number" style={inputStyle} value={f.registration_cost_usd} onChange={set('registration_cost_usd')} /></Field>
          <Field label="Exhibit cost"><input type="number" style={inputStyle} value={f.exhibit_cost_usd} onChange={set('exhibit_cost_usd')} /></Field>
        </div>
        <Field label="Venue"><input style={inputStyle} value={f.venue} onChange={set('venue')} /></Field>
        <Field label="Website"><input style={inputStyle} value={f.website} onChange={set('website')} /></Field>
        <Field label="Notes"><textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={f.description} onChange={set('description')} /></Field>
        {err && <div style={{ color: '#EF4444', fontSize: 11, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn primary onClick={async () => {
            if (!f.name || !f.start_date) { setErr('Name and start date are required.'); return; }
            try {
              const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === '' ? null : v]));
              await crmService.createEvent(body); onCreated(); onClose();
            } catch (e) { setErr(e.message); }
          }}>Create</Btn>
        </div>
      </div>
    </div>
  );
}

// ── TRL ladder tab ───────────────────────────────────────────────────────────
// A Tusk carries one current TRL. That records where it IS, never where it is
// going, so nothing can slip and nothing can be plotted. This is the planned
// ladder: one dated gate per TRL, each with the criteria that decide whether it
// was actually passed.
//
// The office band beside each gate is not decoration. Crossing TRL 7 to 8 moves
// the effort from an RDT&E buyer to an acquisition program office — a change of
// customer, not a change of status — and on the board the milestone changes lane.

const TRL_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const MILESTONE_STATUS = ['planned', 'in_progress', 'achieved', 'slipped', 'descoped'];
const STATUS_COLOR = {
  planned: '#64748B', in_progress: '#22D3EE', achieved: '#059669',
  slipped: '#EF4444', descoped: '#374151',
};

function TrlLadder({ offices }) {
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [draft, setDraft] = useState({ trl_level: '', target_date: '', exit_criteria: '', owner: '' });
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    crmService.getTechMilestones(projectId ? { project_id: projectId } : {})
      .then(r => setMilestones(asList(r)))
      .catch(e => setMsg(e.message));
  }, [projectId]);

  useEffect(() => {
    crmService.getTechProjects().then(r => setProjects(asList(r, 'projects'))).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const current = projects.find(p => String(p.id) === String(projectId));
  const rows = milestones.filter(m => !projectId || String(m.project_id) === String(projectId));

  const add = async () => {
    if (!projectId || !draft.trl_level || !draft.target_date) {
      setMsg('Pick a Tusk, a TRL and a target date.'); return;
    }
    try {
      await crmService.createTechMilestone({ ...draft, project_id: Number(projectId) });
      setDraft({ trl_level: '', target_date: '', exit_criteria: '', owner: '' });
      setMsg(''); load();
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 14, maxWidth: 680, lineHeight: 1.7 }}>
        The dated TRL ladder for each Tusk. Every gate that falls inside the planning window appears
        on the calendar, in the lane of the office that buys at that maturity — so a slipping TRL 6
        shows up next to the pursuits it was supposed to feed.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <select style={{ ...inputStyle, width: 300 }} value={projectId} onChange={e => setProjectId(e.target.value)}>
          <option value="">All Tusks</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}{p.trl_level ? ` (now TRL ${p.trl_level})` : ''}</option>
          ))}
        </select>
        {current?.trl_level && (
          <Chip color={C.accent} title="Current TRL on the Tusk record">
            now TRL {current.trl_level}
          </Chip>
        )}
        {msg && <span style={{ fontSize: 10, color: '#EF4444' }}>{msg}</span>}
      </div>

      {projectId && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap',
          padding: '10px 12px', marginBottom: 14, background: C.panel,
          border: `1px solid ${C.border}`, borderRadius: 6,
        }}>
          <div style={{ width: 90 }}>
            <Field label="TRL gate">
              <select style={inputStyle} value={draft.trl_level}
                onChange={e => setDraft(d => ({ ...d, trl_level: e.target.value }))}>
                <option value="">—</option>
                {TRL_LEVELS.map(l => <option key={l} value={l}>TRL {l}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ width: 150 }}>
            <Field label="Target date">
              <input type="date" style={inputStyle} value={draft.target_date}
                onChange={e => setDraft(d => ({ ...d, target_date: e.target.value }))} />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Field label="Exit criteria — what proves the gate was passed">
              <input style={inputStyle} value={draft.exit_criteria}
                placeholder="Flight test at range, 4 hr endurance, GPS-denied nav within 5 m"
                onChange={e => setDraft(d => ({ ...d, exit_criteria: e.target.value }))} />
            </Field>
          </div>
          <div style={{ width: 130 }}>
            <Field label="Owner">
              <input style={inputStyle} value={draft.owner}
                onChange={e => setDraft(d => ({ ...d, owner: e.target.value }))} />
            </Field>
          </div>
          <div style={{ paddingBottom: 8 }}><Btn small primary onClick={add}>Add gate</Btn></div>
        </div>
      )}

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: C.panel, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '150px 70px 120px 150px 1fr 110px 40px', gap: 10,
          padding: '7px 12px', borderBottom: `1px solid ${C.border}`,
          fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <div>Tusk</div><div>Gate</div><div>Target</div><div>Buying office</div>
          <div>Exit criteria</div><div>Status</div><div />
        </div>
        {rows.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: C.faint, fontSize: 11, lineHeight: 1.7 }}>
            No TRL gates yet.<br />
            Pick a Tusk above and add the gates it has to clear.
          </div>
        )}
        {rows.map(m => (
          <div key={m.id} style={{
            display: 'grid', gridTemplateColumns: '150px 70px 120px 150px 1fr 110px 40px', gap: 10,
            padding: '8px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 11, alignItems: 'center',
          }}>
            <div style={{ color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {m.project_name}
            </div>
            <div><Chip color={C.accent}>TRL {m.trl_level}</Chip></div>
            <div>
              <input type="date" style={{ ...inputStyle, padding: '2px 4px', fontSize: 10 }}
                defaultValue={String(m.target_date).slice(0, 10)}
                onBlur={async e => {
                  if (e.target.value === String(m.target_date).slice(0, 10)) return;
                  await crmService.updateTechMilestone(m.id, { target_date: e.target.value });
                  load();
                }} />
              {m.slip_days > 0 && (
                <div style={{ fontSize: 9, color: '#EF4444' }} title="Measured against the first target ever set">
                  +{m.slip_days}d vs baseline
                </div>
              )}
            </div>
            <div style={{ fontSize: 10, color: C.muted }}>
              {m.office_acronym || m.office_band?.label || '—'}
            </div>
            <div style={{ fontSize: 10, color: m.exit_criteria ? C.muted : C.faint }}>
              {m.exit_criteria || <span title="A TRL date with no exit criteria is a wish, not a plan">no criteria set</span>}
            </div>
            <div>
              <select style={{ ...inputStyle, padding: '2px 4px', fontSize: 10, color: STATUS_COLOR[m.status] }}
                defaultValue={m.status}
                onChange={async e => { await crmService.updateTechMilestone(m.id, { status: e.target.value }); load(); }}>
                {MILESTONE_STATUS.map(st => <option key={st} value={st}>{st.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <span onClick={async () => {
                if (!window.confirm(`Delete the TRL ${m.trl_level} gate?`)) return;
                await crmService.deleteTechMilestone(m.id); load();
              }} style={{ cursor: 'pointer', color: C.faint }}>×</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── triage tab ───────────────────────────────────────────────────────────────

function TriageTab({ onSelect }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    crmService.getCalendarTriage().then(r => setRows(asList(r))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, maxWidth: 680, lineHeight: 1.7 }}>
        Engagements with no office, no acquisition authority, or no appropriation. This list is the
        real product of the weekly refresh — an unclassified engagement is a pursuit nobody has
        thought about yet. Everything here renders grey on the board rather than green, because
        "not yet classified" is not the same as "no fiscal risk".
      </div>
      {loading && <div style={{ color: C.dim, fontSize: 12 }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ padding: 30, textAlign: 'center', color: C.faint, fontSize: 11, border: `1px dashed ${C.border}`, borderRadius: 6 }}>
          Nothing on the triage list. Every engagement in the window carries an office, an
          authority and an appropriation.
        </div>
      )}
      {rows.map(r => (
        <div key={r.id} onClick={() => onSelect({ id: r.id })} style={{
          display: 'flex', gap: 10, alignItems: 'center', padding: '9px 12px', cursor: 'pointer',
          borderBottom: `1px solid ${C.border}`, fontSize: 11,
        }}>
          <span style={{ width: 78, color: C.accent }}>{fmtShort(r.start_at)}</span>
          <span style={{ flex: 1, color: C.text }}>
            {r.title}
            {r.opportunity_name && <span style={{ color: C.faint, fontSize: 9, marginLeft: 6 }}>{r.opportunity_name}</span>}
          </span>
          <span style={{ color: C.muted, fontSize: 10, width: 120 }}>{r.office_acronym || r.office_name || '—'}</span>
          <span style={{ display: 'flex', gap: 4 }}>
            {r.missing.map(m => <Chip key={m} color={C.gold}>no {m}</Chip>)}
          </span>
          <Chip color={C.dim}>{r.source}</Chip>
        </div>
      ))}
    </div>
  );
}

// ── shell ────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'board', label: 'Planning Calendar' },
  { key: 'events', label: 'Events' },
  { key: 'trl', label: 'TRL Ladder' },
  { key: 'triage', label: 'Triage' },
];

export default function BDCalendar({ initialTab = 'board' }) {
  const isHQ = useIsHQ();
  const [tab, setTab] = useState(initialTab);
  const [weeks, setWeeks] = useState(12);   // a quarter — the default planning forecast
  const [scope, setScope] = useState('dod');
  const [streams, setStreams] = useState(['pipeline', 'tusk', 'gov_opportunity', 'event', 'internal']);
  const [groupBy, setGroupBy] = useState('office');
  const [govFilter, setGovFilter] = useState('relevant');
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [refs, setRefs] = useState({ offices: [], authorities: [], appropriations: [] });
  const [opportunities, setOpportunities] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);

  const from = iso(addDays(new Date(), -7));
  const to = iso(addDays(new Date(), weeks * 7));

  const loadBoard = useCallback(() => {
    setLoading(true);
    crmService.getCalendarBoard({ from, to, scope, group_by: groupBy, streams: streams.join(','), gov_filter: govFilter })
      .then(setBoard)
      .catch(() => setBoard(null))
      .finally(() => setLoading(false));
  }, [from, to, scope, groupBy, streams, govFilter]);

  useEffect(loadBoard, [loadBoard]);

  useEffect(() => {
    Promise.all([
      crmService.getOrganizations({ limit: 500 }).catch(() => []),
      crmService.getCalendarAuthorities().catch(() => []),
      crmService.getCalendarAppropriations().catch(() => []),
      crmService.getOpportunities().catch(() => []),
      crmService.getSubsidiaries().catch(() => []),
    ]).then(([orgs, auth, appr, opps, subs]) => {
      setRefs({
        offices: asList(orgs, 'organizations'),
        authorities: asList(auth),
        appropriations: asList(appr),
      });
      setOpportunities(asList(opps, 'opportunities'));
      setSubsidiaries(asList(subs, 'subsidiaries'));
    });
  }, []);

  return (
    <div style={{ padding: '16px 20px', background: C.bg, minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 4 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: C.text, fontWeight: 600, letterSpacing: '-0.01em' }}>Calendar</h2>
          <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>
            Pipeline engagements, TRL gates, solicitation deadlines and events — on one board,
            in the lane of whoever can obligate the money.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {tab === 'board' && <Btn small primary onClick={() => setShowNew(true)}>+ New engagement</Btn>}
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${C.border}`, margin: '14px 0 16px' }}>
        {TABS.map(t => (
          <div key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '7px 14px', cursor: 'pointer', fontSize: 11,
            color: tab === t.key ? '#94E8F7' : C.faint,
            fontWeight: tab === t.key ? 600 : 400,
            borderBottom: `2px solid ${tab === t.key ? C.accent : 'transparent'}`,
          }}>
            {t.label}
            {t.key === 'triage' && board?.summary?.triage_count > 0 && (
              <span style={{ marginLeft: 6, color: C.gold }}>{board.summary.triage_count}</span>
            )}
          </div>
        ))}
      </div>

      {tab === 'board' && (
        <Board board={board} loading={loading} onSelect={setSelected} weeks={weeks} setWeeks={setWeeks}
          scope={scope} setScope={setScope} streams={streams} setStreams={setStreams}
          groupBy={groupBy} setGroupBy={setGroupBy} govFilter={govFilter} setGovFilter={setGovFilter}
          onRefresh={loadBoard} />
      )}
      {tab === 'events' && (
        <EventsTab opportunities={opportunities} subsidiaries={subsidiaries} isHQ={isHQ} />
      )}
      {tab === 'trl' && <TrlLadder offices={refs.offices} />}
      {tab === 'triage' && <TriageTab onSelect={setSelected} />}

      {selected && selected.projected && (
        <ProjectionDrawer mark={selected} onClose={() => setSelected(null)} onPinned={loadBoard} />
      )}
      {selected && !selected.projected && (
        <EngagementDrawer id={selected.id} refs={refs} onClose={() => setSelected(null)} onChanged={loadBoard} />
      )}
      {showNew && (
        <NewEngagement refs={refs} opportunities={opportunities} subsidiaries={subsidiaries}
          onClose={() => setShowNew(false)} onCreated={loadBoard} />
      )}
    </div>
  );
}
